/**
 * Cash Drawer Service
 * WebUSB-based cash drawer controller for thermal printers (XP-80, Epson, Star Micronics, etc.)
 * Works in Chrome/Edge (Chromium) browsers — no server or agent needed.
 *
 * Usage:
 * import { getCashDrawerService } from '@/services/cashDrawerService'
 * const service = getCashDrawerService()
 * await service.openDrawer()
 */

// ── USB filters for WebUSB ─────────────────────────────────────────────────────
// To find your printer's exact IDs: Chrome → chrome://usb-internals/
// Or Windows: Device Manager → your printer → Properties → Details → Hardware IDs
//
const USB_FILTERS = [
  // Specific VID/PID (XP-80 and common thermal printers)
  { vendorId: 0x0483, productId: 0x5740 }, // STMicro / XP-80
  { vendorId: 0x0416, productId: 0x5011 }, // XP-58 / XP-80 alternate
  { vendorId: 0x28e9, productId: 0x0289 }, // common XP vendor
  { vendorId: 0x04b8, productId: 0x0e15 }, // Epson TM series
  { vendorId: 0x04b8, productId: 0x0e28 }, // Epson
  { vendorId: 0x0519, productId: 0x0003 }, // Star Micronics
  { vendorId: 0x0519, productId: 0x0004 },
  { vendorId: 0x1fc9, productId: 0x2016 }, // NXP / receipt printers
  // Any USB printer (interface class 0x07) — catches devices not in the list above
  { classCode: 0x07 },
];

// ── ESC/POS Open Drawer Command ───────────────────────────────────────────────
// ESC p pin on-time off-time
// 1B 70 00 19 FA
// Nothing prints — only triggers the RJ11 drawer pulse.
const OPEN_DRAWER_CMD = new Uint8Array([0x1b, 0x70, 0x00, 0x19, 0xfa]);

class CashDrawerService {
  constructor() {
    this.device = null; // holds the USBDevice instance
    this.paired = false; // true once user has granted USB permission
    this.status = null; // null | 'success' | 'error' | 'loading'
    this.message = "";
    this.isLoading = false;
    this.resetTimer = null;
    this.deviceName = null; // stores the paired device name for UI display
  }

  // ── Check WebUSB support ───────────────────────────────────────────────────

  /**
   * Check if WebUSB is supported in the current browser
   * @returns {boolean} True if WebUSB is available
   */
  isSupported() {
    const ok = !!navigator.usb;
    console.log("[CashDrawerService] isSupported:", ok);
    return ok;
  }

  /**
   * Check if we're in a secure context (HTTPS or localhost)
   * WebUSB open() requires a secure context
   * @returns {boolean} True if secure context
   */
  isSecureContext() {
    return typeof window !== "undefined" && window.isSecureContext;
  }

  /**
   * Check if origin is localhost (any port)
   * Some environments report isSecureContext false but allow localhost
   * @returns {boolean} True if localhost origin
   */
  isLocalhostOrigin() {
    if (typeof window === "undefined" || !window.location) return false;
    const host = window.location.hostname;
    return host === "localhost" || host === "127.0.0.1";
  }

  // ── Request device pairing (called once — opens Chrome USB picker) ─────────
  // Must be triggered by a user gesture (button click).

  /**
   * Request device pairing - opens Chrome USB picker
   * Must be called from a user gesture (button click)
   * @returns {Promise<boolean>} True if pairing successful
   */
  async requestDevice() {
    console.log("[CashDrawerService] requestDevice() called");
    if (!this.isSupported()) {
      console.warn("[CashDrawerService] WebUSB not supported");
      this._setStatus(
        "error",
        "WebUSB is not supported in this browser. Use Chrome or Edge."
      );
      return false;
    }

    try {
      console.log(
        "[CashDrawerService] Showing USB picker with filters:",
        USB_FILTERS
      );
      // Show Chrome's USB device picker to the cashier
      // They select the printer once — Chrome remembers it after that
      this.device = await navigator.usb.requestDevice({ filters: USB_FILTERS });
      this.deviceName = this.device.productName || this.device.manufacturerName || "USB Printer";
      console.log("[CashDrawerService] User selected device:", {
        productName: this.device.productName,
        manufacturerName: this.device.manufacturerName,
        vendorId: this.device.vendorId,
        productId: this.device.productId,
        serialNumber: this.device.serialNumber,
      });
      this.paired = true;
      this._setStatus(
        "success",
        `Printer paired: ${this.deviceName}`
      );
      return true;
    } catch (err) {
      console.warn(
        "[CashDrawerService] requestDevice failed:",
        err.name,
        err.message
      );
      if (err.name === "NotFoundError") {
        this._setStatus(
          "error",
          "No compatible device found. Check: (1) USB cable connected (2) Open chrome://usb-internals — is the printer listed? If not, on Windows uninstall the printer from Settings → Printers so WebUSB can see it."
        );
      } else {
        this._setStatus("error", "Pairing failed: " + err.message);
      }
      return false;
    }
  }

  // ── Reconnect to a previously paired device (no picker needed) ────────────

  /**
   * Reconnect to a previously paired device
   * No user interaction required - automatically reconnects on page load
   * @returns {Promise<boolean>} True if reconnection successful
   */
  async reconnect() {
    console.log("[CashDrawerService] reconnect() called");
    if (!this.isSupported()) {
      console.warn("[CashDrawerService] reconnect skipped: WebUSB not supported");
      return false;
    }

    try {
      const devices = await navigator.usb.getDevices();
      console.log(
        "[CashDrawerService] getDevices() returned",
        devices.length,
        "device(s)"
      );
      if (devices.length > 0) {
        this.device = devices[0];
        this.deviceName = this.device.productName || this.device.manufacturerName || "USB Printer";
        this.paired = true;
        console.log("[CashDrawerService] Reconnected to:", this.deviceName, {
          vendorId: this.device.vendorId,
          productId: this.device.productId,
        });
        return true;
      }
      console.log("[CashDrawerService] No previously paired devices");
    } catch (err) {
      console.warn("[CashDrawerService] reconnect failed:", err.message);
    }
    return false;
  }

  // ── Open the cash drawer ───────────────────────────────────────────────────

  /**
   * Open the cash drawer by sending ESC/POS command via WebUSB
   * @returns {Promise<boolean>} True if drawer opened successfully
   */
  async openDrawer() {
    console.log("[CashDrawerService] openDrawer() called");
    if (this.isLoading) {
      console.log("[CashDrawerService] openDrawer ignored: already loading");
      return false;
    }
    if (this.resetTimer) clearTimeout(this.resetTimer);

    this.isLoading = true;
    this._setStatus(null, "");

    try {
      // Auto-reconnect if we have a paired device but lost the reference
      if (!this.device) {
        console.log(
          "[CashDrawerService] No device reference, attempting reconnect..."
        );
        const reconnected = await this.reconnect();
        if (!reconnected) {
          this._setStatus(
            "error",
            'No printer paired. Click "Pair Printer" first.'
          );
          return false;
        }
      }

      const originOk = this.isSecureContext() || this.isLocalhostOrigin();
      if (!originOk) {
        console.error(
          "[CashDrawerService] Not a secure context. Current origin:",
          window.location.origin
        );
        this._setStatus(
          "error",
          "USB open requires HTTPS or http://localhost. You are on: " +
            window.location.origin
        );
        return false;
      }
      console.log(
        "[CashDrawerService] Opening USB connection... (origin:",
        window.location.origin,
        ", secure:",
        this.isSecureContext(),
        ", localhost:",
        this.isLocalhostOrigin(),
        ")"
      );
      await this.device.open();

      if (this.device.configuration === null) {
        console.log("[CashDrawerService] Selecting configuration 1");
        await this.device.selectConfiguration(1);
      }

      console.log("[CashDrawerService] Claiming interface 0");
      await this.device.claimInterface(0);

      const iface = this.device.configuration.interfaces[0];
      const alternate = iface.alternates[0];
      const endpoint = alternate.endpoints.find((e) => e.direction === "out");

      if (!endpoint) {
        console.error(
          "[CashDrawerService] No OUT endpoint. Endpoints:",
          alternate.endpoints
        );
        throw new Error("No OUT endpoint found on printer USB interface.");
      }
      console.log(
        "[CashDrawerService] Using OUT endpoint:",
        endpoint.endpointNumber
      );

      console.log("[CashDrawerService] Sending open-drawer command (5 bytes)...");
      await this.device.transferOut(endpoint.endpointNumber, OPEN_DRAWER_CMD);

      console.log("[CashDrawerService] Releasing interface and closing device");
      await this.device.releaseInterface(0);
      await this.device.close();

      this._setStatus("success", "Cash drawer opened!");
      console.log("[CashDrawerService] openDrawer completed successfully");
      return true;
    } catch (err) {
      // Try to clean up if something went wrong mid-transfer
      try {
        if (this.device?.opened) {
          await this.device.releaseInterface(0).catch(() => {});
          await this.device.close().catch(() => {});
        }
      } catch (_) {}

      console.error(
        "[CashDrawerService] openDrawer error:",
        err.name,
        err.message,
        "origin:",
        window.location?.origin,
        "secureContext:",
        window.isSecureContext,
        err
      );

      if (err.message?.includes("Unable to claim interface")) {
        this._setStatus(
          "error",
          "Cannot claim printer. Close any other app using the printer (e.g. receipt software)."
        );
      } else if (err.name === "NetworkError") {
        this._setStatus(
          "error",
          "USB transfer failed. Unplug and replug the printer, then try again."
        );
      } else if (err.name === "SecurityError") {
        const origin =
          typeof window !== "undefined" ? window.location.origin : "unknown";
        console.warn(
          "[CashDrawerService] SecurityError: Check chrome://device-log for details. On Windows, the device often needs the WinUSB driver (use Zadig: https://zadig.akeo.ie/)."
        );
        this._setStatus(
          "error",
          "Access denied. On Windows: install WinUSB driver for this device (Zadig). See chrome://device-log for details."
        );
      } else {
        this._setStatus("error", err.message || "Unknown error opening drawer.");
      }
      return false;
    } finally {
      this.isLoading = false;
      this.resetTimer = setTimeout(() => {
        this.status = null;
        this.message = "";
      }, 4000);
    }
  }

  // ── Internal helper ────────────────────────────────────────────────────────

  /**
   * Set status and message
   * @private
   * @param {string|null} s - Status value
   * @param {string} m - Message
   */
  _setStatus(s, m) {
    this.status = s;
    this.message = m;
  }

  /**
   * Get current status
   * @returns {Object} Status object with status, message, isLoading, paired, deviceName
   */
  getStatus() {
    return {
      status: this.status,
      message: this.message,
      isLoading: this.isLoading,
      paired: this.paired,
      deviceName: this.deviceName,
    };
  }
}

// Create singleton instance
let cashDrawerService = null;

/**
 * Get or create CashDrawerService instance
 * @returns {CashDrawerService} CashDrawerService instance
 */
export function getCashDrawerService() {
  if (!cashDrawerService) {
    cashDrawerService = new CashDrawerService();
  }
  return cashDrawerService;
}

export default CashDrawerService;
